import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { confirmStartingPoint } from "@/lib/api";
import { toast } from "sonner";
import { SparkSun } from "@/components/SparkSun";
import { useSunRays } from "@/lib/sunRays";
import { TalkInput } from "@/components/TalkInput";

export default function StartingPoint() {
  const { userId, profile, setProfile } = useSpark();
  const nav = useNavigate();
  const [mode, setMode] = useState(null); // null | partly | no | done
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const { earned, fresh } = useSunRays(profile, userId);

  if (!profile) return <div className="text-[#8A8A91]">Loading…</div>;
  if (!profile.starting_point_summary) {
    return (
      <div className="max-w-2xl">
        <p className="text-[#56565D]">No Starting Point yet.</p>
        <button onClick={() => nav("/onboarding")} className="spark-btn-primary mt-4">Begin onboarding</button>
      </div>
    );
  }

  const respond = async (status) => {
    setSaving(true);
    try {
      const p = await confirmStartingPoint(userId, { status, correction });
      setProfile(p);
      if (status === "yes") {
        setMode("done");
      } else {
        toast("Rewritten with your correction.");
        setMode(null);
        setCorrection("");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Your Starting Point</p>
      <h1 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">
        Here's what I'm hearing.
      </h1>

      <div className="mt-10 spark-voice-block">
        <p
          data-testid="starting-point-summary"
          className="spark-voice text-2xl sm:text-3xl leading-snug text-[#17171C]"
        >
          {profile.starting_point_summary}
        </p>
      </div>

      {profile.starting_point_confirmed !== "yes" && mode !== "partly" && mode !== "no" && (
        <>
          <p className="mt-8 text-[#56565D]">Does that feel right?</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="starting-point-yes-btn" disabled={saving} onClick={() => respond("yes")} className="spark-btn-copper">Yes, that's right</button>
            <button data-testid="starting-point-partly-btn" onClick={() => setMode("partly")} className="spark-btn-secondary">Partly</button>
            <button data-testid="starting-point-no-btn" onClick={() => setMode("no")} className="spark-btn-secondary">No, let me explain</button>
          </div>
        </>
      )}

      {(mode === "partly" || mode === "no") && (
        <div className="mt-8">
          <p className="text-[#56565D] mb-3">
            {mode === "partly" ? "Which part fits, and which part doesn't?" : "Tell me what I'm missing."}
          </p>
          <TalkInput testid="starting-point-correction-input" rows={5} value={correction} onChange={setCorrection} placeholder="In your own words." />
          <div className="mt-3 flex gap-2">
            <button
              data-testid="starting-point-submit-correction-btn"
              disabled={saving || !correction.trim()}
              onClick={() => respond(mode)}
              className="spark-btn-primary disabled:opacity-60"
            >
              {saving ? "Rewriting…" : "Rewrite it"}
            </button>
            <button onClick={() => { setMode(null); setCorrection(""); }} className="spark-btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {mode === "done" && (
        <div data-testid="starting-point-sun-moment" className="mt-12 grid sm:grid-cols-[auto_1fr] gap-8 items-center rounded-3xl p-8"
             style={{ background: "hsl(var(--spark-warmth))" }}>
          <SparkSun size={150} earned={earned} fresh={fresh} color="#C3A56B" strokeWidth={1} title="Your Spark Sun" testId="spark-sun-moment" />
          <div>
            <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Your Sun</p>
            <p className="mt-3 spark-voice text-2xl sm:text-3xl leading-snug text-[#17171C]">
              A ray completes. Your Starting Point is now part of your mark.
            </p>
            <p className="mt-4 text-sm text-[#56565D] leading-relaxed max-w-md">
              Each time you confirm something Spark has understood — a Starting Point, a strength,
              a Gold Thread — another ray is drawn. This mark is built from what you say is true.
            </p>
          </div>
        </div>
      )}

      {profile.starting_point_confirmed === "yes" && mode !== "done" && (
        <div className="mt-8 flex items-center gap-3 text-sm text-[#8A8A91]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#2C50A0]" />
          Held. Spark will carry this into everything that follows.
        </div>
      )}

      <div className="mt-14 flex flex-wrap gap-3">
        <button data-testid="starting-point-go-dashboard-btn" onClick={() => nav("/dashboard")} className="spark-btn-secondary">Go to my Journey</button>
        <button data-testid="starting-point-go-part-one-btn" onClick={() => nav("/part-one")} className="spark-btn-primary">Continue to Part One</button>
      </div>
    </div>
  );
}
